import { Alert, Button, MessagePanel } from '@dittolive/anvil'
import graphql from 'babel-plugin-relay/macro'
import { useAppToDatabaseRename } from 'components/hooks'
import React, { useState } from 'react'
import { useFragment } from 'react-relay/hooks'
import { usePortalMutation } from 'utils'

import { TransferAppDestination_transferRequestDestination$key } from './__generated__/TransferAppDestination_transferRequestDestination.graphql'
import { TransferAppDestinationCancelMutation } from './__generated__/TransferAppDestinationCancelMutation.graphql'

const fragment = graphql`
  fragment TransferAppDestination_transferRequestDestination on AppDestination {
    ... on UserDestination {
      email
      __typename
    }
    ... on Organization {
      # eslint-disable-next-line relay/unused-fields
      id
      name
      __typename
    }
  }
`

const cancelTransferMutation = graphql`
  mutation TransferAppDestinationCancelMutation($appId: ID!) {
    cancelAppTransferRequest(appId: $appId) {
      transferRequestDestination {
        __typename
      }
    }
  }
`

type Props = {
  /** App ID */
  appId: string
  /** Destination entity */
  destination: TransferAppDestination_transferRequestDestination$key
  /** True if the user is allowed to cancel the transfer request. */
  canCancel: boolean
}

const TransferAppDestination: React.FC<Props> = ({
  destination,
  appId,
  canCancel,
}) => {
  const data = useFragment(fragment, destination)

  const { databaseText } = useAppToDatabaseRename()

  const [cancelTransferRequest, isCancelingTransferRequest] =
    usePortalMutation<TransferAppDestinationCancelMutation>(
      cancelTransferMutation,
      {
        mutationName: 'CancelAppTransferRequest',
        sentryContext: {
          component: 'TransferAppDestination',
          appId,
        },
        showErrorToast: false,
      },
    )
  const [cancelRequestError, setCancelRequestError] = useState<Error>()

  const handleCancelTransferRequest = () => {
    cancelTransferRequest({
      variables: { appId },
      onError: setCancelRequestError,
    })
  }

  const destinationTypeName =
    data?.__typename === 'Organization' ? 'organization' : 'user'

  const destinationName =
    data?.__typename === 'Organization'
      ? data.name
      : data.__typename === 'UserDestination'
        ? data.email
        : null

  return (
    <>
      {!!cancelRequestError && (
        <Alert
          variant="danger"
          autoDismiss={false}
        >{`Error cancelling the transfer request: ${cancelRequestError.message}`}</Alert>
      )}

      <MessagePanel
        variant="danger"
        message={() => (
          <>
            This {databaseText} is pending transfer to {destinationTypeName}{' '}
            <span className="font-medium">{`"${destinationName}"`}</span>. If
            you want to cancel this request you must do so before the transfer
            is accepted by the recipient.
          </>
        )}
      />

      {!!canCancel && (
        <Button
          variant="critical"
          onClick={handleCancelTransferRequest}
          disabled={isCancelingTransferRequest}
          className="mt-8"
        >
          Cancel request
        </Button>
      )}
    </>
  )
}

export default TransferAppDestination
